/**
 * Suspensiones — BackOffice (Fase 4.5).
 *
 * Contrapartida de la política de destrucción: desde jul 2026 los perfiles que
 * no son nivel Dios ya no eliminan, SUSPENDEN. Este panel es donde eso se ve y
 * se revierte; sin él la suspensión sería un agujero negro.
 *
 * Reglas que refleja la pantalla:
 *   - VER: cualquier administrador (es información de su propia empresa).
 *   - REACTIVAR: solo nivel Dios. El backend manda `puede_reactivar` y aquí
 *     solo se decide qué mostrar — la autorización real la aplica
 *     `require_platform_owner` en el servidor.
 *   - Lo archivado por diseño (una campaña archivada) NO se reactiva: se marca
 *     de forma distinta y sin botón.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Archive, ArchiveRestore, Info, Loader2, RefreshCcw, ShieldAlert, Undo2, Trash2,
} from 'lucide-react';

const COLOR_CATEGORIA = {
  negocio: 'text-cyan-300 border-cyan-500/30 bg-cyan-500/10',
  catalogo: 'text-amber-300 border-amber-500/30 bg-amber-500/10',
  credencial: 'text-rose-300 border-rose-500/30 bg-rose-500/10',
  operativo: 'text-white/60 border-white/15 bg-white/5',
};

const fechaLegible = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export default function Suspensiones() {
  const [cargando, setCargando] = useState(true);
  const [datos, setDatos] = useState({ total: 0, grupos: [], puede_reactivar: false });
  const [reactivando, setReactivando] = useState({});

  const [purgando, setPurgando] = useState({});

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get('/suspensions');
      setDatos(r.data || { total: 0, grupos: [], puede_reactivar: false });
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      toast.error(typeof detalle === 'string' ? detalle : 'No se pudieron cargar las suspensiones');
      setDatos({ total: 0, grupos: [], puede_reactivar: false });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const purgarCuenta = async (item) => {
    const ok = window.confirm(
      `ELIMINAR DEFINITIVO "${item.nombre}"\n\nSe borran usuarios, leads, WhatsApp, recetas y todo lo de esa cuenta. No hay vuelta atrás.`,
    );
    if (!ok) return;
    const otra = window.prompt(`Escribe ELIMINAR para confirmar la baja de "${item.nombre}"`);
    if (otra !== 'ELIMINAR') {
      toast.error('No se eliminó. Tenías que escribir ELIMINAR.');
      return;
    }
    setPurgando((s) => ({ ...s, [item.id]: true }));
    try {
      await api.delete(`/admin/companies/${item.id}`);
      toast.success(`"${item.nombre}" se eliminó por completo`);
      cargar();
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      toast.error(typeof detalle === 'string' ? detalle : 'No se pudo eliminar');
    } finally {
      setPurgando((s) => ({ ...s, [item.id]: false }));
    }
  };

  const reactivar = async (tipo, item) => {
    if (!window.confirm(
      `¿Reactivar "${item.nombre}"?\n\nVolverá a aparecer en los listados y en la operación normal.`
    )) return;

    setReactivando((s) => ({ ...s, [item.id]: true }));
    try {
      await api.post(`/suspensions/${tipo}/${item.id}/reactivate`);
      toast.success(`"${item.nombre}" volvió a la operación`);
      cargar();
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      toast.error(typeof detalle === 'string' ? detalle : 'No se pudo reactivar');
    } finally {
      setReactivando((s) => ({ ...s, [item.id]: false }));
    }
  };

  return (
    <div className="space-y-5" data-testid="suspensiones-page">
      {/* Encabezado */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-heading font-bold text-white flex items-center gap-2">
            <Archive className="w-5 h-5 text-amber-400" />
            Suspensiones
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Todo lo que fue retirado de la operación sin borrarse. Nada de esto se perdió.
            Lo <b className="text-white/80">suspendido</b> puede volver; lo{' '}
            <b className="text-white/80">archivado</b> se conserva completo pero no se
            reactiva, para no dejar registros apuntando a algo que ya no opera.
            {datos.alcance === 'plataforma' && (
              <span className="text-cyan-300/80"> Estás viendo todas las empresas.</span>
            )}
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={cargar} disabled={cargando}
          className="border-white/10 text-xs" data-testid="susp-refrescar"
        >
          <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${cargando ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Aviso de permisos para quien no es nivel Dios */}
      {!cargando && !datos.puede_reactivar && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2"
          data-testid="susp-aviso-permisos"
        >
          <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-200/90">
            Puedes consultar lo suspendido, pero solo el Owner de la plataforma puede devolverlo
            a la operación.
          </p>
        </div>
      )}

      {cargando ? (
        <Card className="bg-card/50 border-white/5">
          <CardContent className="py-16 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          </CardContent>
        </Card>
      ) : datos.total === 0 ? (
        <Card className="bg-card/50 border-white/5" data-testid="susp-vacio">
          <CardContent className="py-16 text-center">
            <ArchiveRestore className="w-12 h-12 mx-auto text-muted-foreground/25 mb-4" />
            <p className="text-sm text-white/70">No hay nada suspendido</p>
            <p className="text-xs text-muted-foreground mt-1">
              Cuando alguien retire un registro de la operación, aparecerá aquí para poder revisarlo.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-xs text-muted-foreground" data-testid="susp-total">
            <strong className="text-white">{datos.total}</strong>{' '}
            {datos.total === 1 ? 'elemento suspendido' : 'elementos suspendidos'}
          </p>

          {datos.grupos.map((grupo) => (
            <Card key={grupo.tipo} className="bg-card/50 border-white/5" data-testid={`susp-grupo-${grupo.tipo}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-white">{grupo.etiqueta}</h3>
                  <Badge variant="outline" className={`text-[9px] ${COLOR_CATEGORIA[grupo.categoria] || COLOR_CATEGORIA.operativo}`}>
                    {grupo.categoria}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground">
                    {grupo.total} {grupo.total === 1 ? 'elemento' : 'elementos'}
                  </span>
                </div>

                <div className="space-y-2">
                  {grupo.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-black/20 border border-white/5 flex-wrap"
                      data-testid={`susp-item-${item.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-white font-medium truncate">{item.nombre}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {item.empresa ? <span className="text-cyan-300/80">{item.empresa} · </span> : null}
                          {item.archivada_por_diseno ? 'Archivado' : 'Suspendido'} el {fechaLegible(item.suspended_at)}
                          {item.suspended_by_name ? ` por ${item.suspended_by_name}` : ''}
                        </p>
                        {item.suspended_reason && (
                          <p className="text-[10px] text-white/50 mt-0.5 italic">
                            Motivo: {item.suspended_reason}
                          </p>
                        )}
                      </div>

                      {item.archivada_por_diseno ? (
                        <span
                          className="text-[9px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-muted-foreground flex items-center gap-1 shrink-0"
                          title="Se archivó de forma definitiva para no dejar registros huérfanos apuntando a ella. No se puede reactivar: si la necesitas de vuelta, crea una nueva."
                          data-testid={`susp-definitiva-${item.id}`}
                        >
                          <Info className="w-3 h-3" /> Archivada definitivamente
                        </span>
                      ) : datos.puede_reactivar ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => reactivar(grupo.tipo, item)}
                            disabled={!!reactivando[item.id] || !!purgando[item.id]}
                            className="h-7 text-[10px] border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`susp-reactivar-${item.id}`}
                          >
                            {reactivando[item.id] ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Undo2 className="w-3 h-3 mr-1" />
                            )}
                            Reactivar
                          </Button>
                          {grupo.tipo === 'company' && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => purgarCuenta(item)}
                              disabled={!!purgando[item.id]}
                              className="h-7 text-[10px] border-red-500/30 text-red-300 hover:bg-red-500/10"
                              data-testid={`susp-purgar-${item.id}`}
                            >
                              {purgando[item.id] ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3 mr-1" />
                              )}
                              Eliminar
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
